import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { lookupGroupBooking } from '../services/groupBookingService';
import { LookupGroupBookingResponse } from '../types/groupBooking';
import { MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { formatGroupDate, formatGroupDateTime } from '../utils/dateUtils';

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'pending': return '승인대기';
    case 'confirmed': return '예약확정';
    case 'cancelled': return '예약취소';
    default: return status;
  }
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'confirmed': return 'bg-green-50 text-green-700 border-green-200';
    case 'cancelled': return 'bg-gray-100 text-gray-500 border-gray-200';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const formatDate = formatGroupDate;
const formatDateTime = formatGroupDateTime;

const GroupBookingLookup: React.FC = () => {
  const [bookingNumber, setBookingNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupGroupBookingResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLookup = async () => {
    setError('');
    setResult(null);

    if (!bookingNumber.trim()) { setError('예약번호를 입력해주세요.'); return; }
    if (!phone.trim()) { setError('예약자 휴대폰번호를 입력해주세요.'); return; }

    setLoading(true);
    const res = await lookupGroupBooking(bookingNumber.trim(), phone.trim());
    setLoading(false);

    if (res.success) {
      setResult(res);
    } else {
      setError(res.error?.message || '조회에 실패했습니다.');
    }
  };

  const booking = result?.data;

  return (
    <>
      <SEO
        title="단체체험 예약 조회"
        description="동두천시 글로벌인재교육센터 단체체험 예약 상태 조회"
        path="/application/group/lookup"
      />
      <div className="min-h-screen flex flex-col">
        <Header />

        {/* Page Header */}
        <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1920&h=600&fit=crop&q=80"
              alt="예약 조회"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">수강신청</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">예약 조회</h1>
            <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 py-8 sm:py-12 bg-gray-50">
          <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
            {!booking ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-800 mb-1">단체체험 예약 조회</h2>
                <p className="text-sm text-gray-500 mb-6">신청 시 받은 예약번호와 예약자 휴대폰번호를 입력해주세요.</p>

                <form onSubmit={e => { e.preventDefault(); handleLookup(); }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">예약번호</label>
                    <input
                      type="text"
                      value={bookingNumber}
                      onChange={e => setBookingNumber(e.target.value)}
                      placeholder="GR-20260302-00001"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">예약자 휴대폰번호</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="010-1234-5678"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                      loading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    {loading ? '조회 중...' : '예약 조회'}
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
                <div className="text-center mb-6">
                  <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <h2 className="text-xl font-bold text-gray-800">예약 조회 결과</h2>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">예약번호</span>
                      <span className="font-mono font-bold text-green-700">{booking.booking_number}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">상태</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusStyle(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">프로그램</span>
                      <span className="font-medium text-gray-800">{booking.program_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">단체명</span>
                      <span className="text-gray-800">{booking.institution_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">예약자</span>
                      <span className="text-gray-800">{booking.booker_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">신청일시</span>
                      <span className="text-gray-800">{formatDateTime(booking.created_at)}</span>
                    </div>
                    {booking.confirmed_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">확정일시</span>
                        <span className="text-green-700">{formatDateTime(booking.confirmed_at)}</span>
                      </div>
                    )}
                    {booking.cancelled_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">취소일시</span>
                        <span className="text-red-600">{formatDateTime(booking.cancelled_at)}</span>
                      </div>
                    )}
                  </div>

                  {/* 예약 날짜 */}
                  <div>
                    <span className="text-sm text-gray-500 block mb-1.5">예약 날짜</span>
                    <div className="flex flex-wrap gap-1.5">
                      {booking.booking_dates.sort().map(d => (
                        <span key={d} className="px-2.5 py-1 bg-green-50 text-green-700 rounded text-xs font-medium border border-green-200">
                          {formatDate(d)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* 참가자 */}
                  <div>
                    <span className="text-sm text-gray-500 block mb-1.5">
                      참가자 ({booking.participants.type === 'preschool' ? '유치원' : '초등'} / 총 {booking.participants.total_count}명)
                    </span>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      {booking.participants.groups.map((g, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span className="text-gray-600">{g.label}</span>
                          <span className="font-semibold text-gray-800">{g.count}명</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 안내 */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800 font-medium mb-1">
                      ※ 문의: 031-866-5670
                    </p>
                    {booking.status === 'pending' && (
                      <p className="text-sm text-amber-800">
                        ※ 담당자 확인 후 예약이 확정됩니다.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => { setResult(null); setBookingNumber(''); setPhone(''); }}
                    className="w-full py-3 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm hover:bg-gray-200 transition-colors"
                  >
                    다른 예약 조회
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default GroupBookingLookup;
